
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AIStaffMember, AIStaffRole, AIStaffLevel, Achievement } from "@/lib/types";
import { useQueryClient } from "@tanstack/react-query";

export function useAIStaffCreation() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiStaff, setAiStaff] = useState<AIStaffMember | null>(null);
  const queryClient = useQueryClient();

  const createAIStaff = async (selectedCandidate: number | null) => {
    if (!selectedCandidate) {
      toast({
        title: "Error",
        description: "Please select a candidate first",
        variant: "destructive",
      });
      return null;
    }
    
    setIsSubmitting(true);
    
    try {
      // Get current user session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        throw new Error("You must be logged in to complete this action");
      }
      
      // Get business ID
      const { data: businessData, error: businessError } = await supabase
        .from('business')
        .select('id')
        .eq('owner_id', sessionData.session.user.id)
        .maybeSingle();
      
      if (businessError || !businessData) {
        throw new Error("Could not find your business profile");
      }
      
      const candidate = selectedCandidate === 1 ? 
        { name: "Morgan", role: "marketing" as const, level: "junior" as const } :
        { name: "Alex", role: "sales" as const, level: "junior" as const };
      
      // Insert the AI staff with correct typing
      const { data: staffData, error: staffError } = await supabase
        .from('ai_staff')
        .insert({
          business_id: businessData.id,
          name: candidate.name,
          role: candidate.role,
          level: candidate.level,
          experience: 0,
          skill_points: 10,
          abilities: candidate.role === "marketing" 
            ? ["Social Media Post Creation", "Email Campaign Creation", "Basic Content Generation"] 
            : ["Lead Generation", "Sales Scripting", "Basic Outreach"],
          achievements: [],
          is_active: true
        })
        .select()
        .single();
      
      if (staffError || !staffData) {
        throw new Error(`Failed to create AI staff: ${staffError?.message || 'Unknown error'}`);
      }
      
      // Convert and set the newly created AI staff
      const achievements = ((staffData.achievements as any[]) || []).map((achievement): Achievement => ({
        id: achievement.id || '',
        name: achievement.name || '',
        description: achievement.description || '',
        icon: achievement.icon || '🏆',
        unlockedAt: achievement.unlockedAt ? new Date(achievement.unlockedAt) : undefined,
        isUnlocked: achievement.isUnlocked || false
      }));

      const convertedStaff: AIStaffMember = {
        id: staffData.id,
        name: staffData.name,
        role: staffData.role,
        level: staffData.level,
        experience: staffData.experience,
        skillPoints: staffData.skill_points,
        abilities: staffData.abilities,
        avatar: staffData.avatar || undefined,
        hiredAt: new Date(staffData.hired_at),
        achievements: achievements,
        is_active: true
      };
      
      setAiStaff(convertedStaff);
      
      // Immediately invalidate related queries to refresh the data across the app
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      queryClient.invalidateQueries({ queryKey: ['allStaff'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
      
      return convertedStaff;
      
    } catch (error: any) {
      console.error("Error creating AI staff:", error);
      
      toast({
        title: "Error",
        description: error.message || "Failed to create AI staff",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchAIStaff = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData.session) return;
      
      const { data: businessData } = await supabase
        .from('business')
        .select('id')
        .eq('owner_id', sessionData.session.user.id)
        .single();
      
      if (!businessData) return;
      
      const { data: staffData, error } = await supabase
        .from('ai_staff')
        .select('*')
        .eq('business_id', businessData.id)
        .eq('is_active', true)
        .maybeSingle();
      
      if (error) {
        console.error("Error fetching AI staff:", error);
        return;
      }
      
      if (staffData) {
        const achievements = ((staffData.achievements as any[]) || []).map((achievement): Achievement => ({
          id: achievement.id || '',
          name: achievement.name || '',
          description: achievement.description || '',
          icon: achievement.icon || '🏆',
          unlockedAt: achievement.unlockedAt ? new Date(achievement.unlockedAt) : undefined,
          isUnlocked: achievement.isUnlocked || false
        }));

        const convertedStaff: AIStaffMember = {
          id: staffData.id,
          name: staffData.name,
          role: staffData.role as AIStaffRole,
          level: staffData.level as AIStaffLevel,
          experience: staffData.experience,
          skillPoints: staffData.skill_points,
          abilities: staffData.abilities,
          avatar: staffData.avatar || undefined,
          hiredAt: new Date(staffData.hired_at),
          achievements: achievements,
          is_active: true
        };
        
        setAiStaff(convertedStaff);
      }
    } catch (error) {
      console.error("Error fetching AI staff:", error);
    }
  };

  return {
    isSubmitting,
    aiStaff,
    setAiStaff,
    createAIStaff,
    fetchAIStaff
  };
}
